import { denormalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import CrmObjectRecord from 'customer-data-objects/crmObject/CrmObjectRecord';
import useNewPropertiesForm from 'customer-data-properties/form/useNewPropertiesForm';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isVisibleProperty } from '../../utils/isVisibleProperty';
import { usePropertiesCardContext } from './PropertiesCardContext';
import PropertiesCardInputs from './PropertiesCardInputs';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const trackPropertiesSave = ({
  values,
  objectTypeId
}) => {
  const eventProps = {
    objectType: denormalizeTypeId(objectTypeId)
  };
  if (Object.prototype.hasOwnProperty.call(values, 'dealstage')) {
    eventProps.type = 'Deal Stage';
  }
  const updatesArray = Object.keys(values);
  CrmLogger.log('saved-properties', Object.assign({
    properties: updatesArray.length ? updatesArray.join(' ') : undefined
  }, eventProps));
};
const PropertiesCardContent = () => {
  const {
    cardId,
    crmObject,
    objectId,
    objectTypeId,
    onObjectUpdate,
    onSaveSuccess,
    propertyNames,
    SuccessMarker,
    CardContentLoadingComponent,
    updateCrmObjectCache
  } = usePropertiesCardContext();
  const [values, setValues] = useState({});
  const trackingChangesRef = useRef({});
  const pendingUpdatesRef = useRef({});
  const {
    allPropertiesMap
  } = crmObject;
  const subject = CrmObjectRecord.fromJS({
    objectId,
    properties: Object.fromEntries(allPropertiesMap)
  });
  const propertiesToRender = propertyNames.filter(name => {
    const property = allPropertiesMap.get(name);
    if (property && isVisibleProperty(property)) {
      return true;
    }
    return false;
  }).map(name => ({
    propertyName: name
  }));
  const getSavedValue = useCallback(propertyName => {
    if (Object.prototype.hasOwnProperty.call(pendingUpdatesRef.current, propertyName)) {
      return pendingUpdatesRef.current[propertyName];
    }
    const property = allPropertiesMap.get(propertyName);
    return property ? property.value : undefined;
  }, [allPropertiesMap]);
  useEffect(() => {
    // When the GQL cache is updated, we can clear out any pending updates that are now reflected in the cache
    Object.keys(pendingUpdatesRef.current).forEach(key => {
      var _crmObject$allPropert;
      if (((_crmObject$allPropert = crmObject.allPropertiesMap.get(key)) === null || _crmObject$allPropert === void 0 ? void 0 : _crmObject$allPropert.value) === pendingUpdatesRef.current[key]) {
        delete pendingUpdatesRef.current[key];
      }
    });
  }, [crmObject]);
  const handleChange = useCallback(newChanges => {
    setValues(newChanges);
    trackingChangesRef.current = newChanges;
  }, []);
  const handleSave = useCallback(updates => {
    const savedValues = updates[objectId];
    if (savedValues) {
      onSaveSuccess === null || onSaveSuccess === void 0 || onSaveSuccess();
      onObjectUpdate === null || onObjectUpdate === void 0 || onObjectUpdate({
        objectTypeId,
        objectId,
        properties: savedValues
      });

      // We locally update the GQL cache so changes are picked up, but this can take a few render cycles
      // before the new values are picked up. If we only rely on this and not the pendingUpdatesRef, we risk
      // flickering the old value.
      updateCrmObjectCache(savedValues);
      pendingUpdatesRef.current = Object.assign({}, pendingUpdatesRef.current, savedValues);
      trackPropertiesSave({
        values: trackingChangesRef.current,
        objectTypeId
      });
      // Only clear draft values that were saved and have not changed
      setValues(prev => {
        const newValues = Object.assign({}, prev);
        Object.keys(savedValues).forEach(key => {
          if (prev[key] === savedValues[key]) {
            delete newValues[key];
          }
        });
        return newValues;
      });
    }
  }, [objectId, onSaveSuccess, onObjectUpdate, objectTypeId, updateCrmObjectCache]);
  const {
    status,
    inputs
  } = useNewPropertiesForm({
    getSavedValue,
    objectId: objectId.toString(),
    objectTypeId,
    onChange: handleChange,
    properties: propertiesToRender,
    values,
    enableAutosave: true,
    onSave: handleSave
  });
  if (!propertiesToRender.length) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "cardProperties.emptyState"
      }), SuccessMarker && /*#__PURE__*/_jsx(SuccessMarker, {})]
    });
  }
  return /*#__PURE__*/_jsx("div", {
    "data-properties-card-id": cardId,
    "data-selenium-test": "properties-card-content"
    // Used for onboarding tours, #growth-onboarding for more details
    ,
    "data-onboarding": "properties-card-content",
    children: status.validationStatus !== 'INITIALIZING' ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(PropertiesCardInputs, {
        inputs: inputs,
        allProperties: allPropertiesMap,
        subject: subject
      }), SuccessMarker && /*#__PURE__*/_jsx(SuccessMarker, {})]
    }) : /*#__PURE__*/_jsx(CardContentLoadingComponent, {
      cardId: cardId,
      numberOfSkeletons: propertiesToRender.length
    })
  });
};
export default PropertiesCardContent;